import { Button } from "@frontend/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@frontend/components/ui/card"
import { DataTable } from "@frontend/components/ui/data-table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { useApiClient, useApiQuery } from "@frontend/hooks/use-api"
import { useInstanceMembers, useRemoveInstanceMember, useUpdateInstanceMemberRole } from "@frontend/hooks/use-instance"
import { useProjects } from "@frontend/hooks/use-projects"
import { InviteMemberDialogContainer } from "@frontend/pages/instance-members/components/invite-member-dialog"
import { useQuery } from "@tanstack/react-query"
import type { ColumnDef } from "@tanstack/react-table"
import { ArrowLeft, Trash2 } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { Link } from "react-router"

interface Member {
  id: string
  role: "admin" | "member"
  created_at: string
  user_id: string
  email: string
  name: string
}

interface Project {
  id: string
  name: string
}

interface ProjectMember {
  user_id: string
  role: string
}

interface RoleMatrixRow {
  member: Member
}

export function InstanceMembers() {
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const { data: membersResult, isLoading } = useInstanceMembers()
  const { data: projectsResult } = useProjects({ limit: 100 })
  const { data: currentUser } = useApiQuery<{ id: string }>(["auth", "me"], "/auth/me")
  const updateRole = useUpdateInstanceMemberRole()
  const removeMember = useRemoveInstanceMember()
  const api = useApiClient()

  const members: Member[] = membersResult?.data ?? []
  const projects: Project[] = projectsResult?.data ?? []
  const currentUserId = currentUser?.id

  const isAdmin = members.some((m) => m.user_id === currentUserId && m.role === "admin")

  const { data: projectMembersMap } = useQuery({
    queryKey: ["instance", "members", "role-matrix"],
    queryFn: async () => {
      const result: Record<string, Record<string, string>> = {}
      for (const project of projects) {
        const res = (await api(`/projects/${project.id}/members?limit=100`)) as { data?: ProjectMember[] }
        result[project.id] = {}
        for (const pm of res.data ?? []) {
          result[project.id]![pm.user_id] = pm.role
        }
      }
      return result
    },
    enabled: isAdmin && projects.length > 0
  })

  const handleRoleChange = useCallback(
    (memberId: string, role: "admin" | "member") => {
      updateRole.mutate({ memberId, role })
    },
    [updateRole.mutate]
  )

  const handleRemove = useCallback(
    (memberId: string) => {
      if (confirm("Are you sure you want to remove this member?")) {
        removeMember.mutate(memberId)
      }
    },
    [removeMember.mutate]
  )

  const memberColumns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span className="text-sm font-medium">{row.original.name}</span>
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.email}</span>
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: ({ row }) =>
          isAdmin ? (
            <Select
              value={row.original.role}
              onValueChange={(value) => handleRoleChange(row.original.id, value as "admin" | "member")}
            >
              <SelectTrigger className="w-32" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <span className="capitalize">{row.original.role}</span>
          )
      },
      ...(isAdmin
        ? [
            {
              id: "actions",
              accessorFn: (row) => row.id,
              header: "Actions",
              cell: ({ row }) => (
                <div className="text-right">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove member"
                    onClick={() => handleRemove(row.original.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              )
            } as ColumnDef<Member>
          ]
        : [])
    ],
    [isAdmin, handleRemove, handleRoleChange]
  )

  const roleMatrixColumns = useMemo<ColumnDef<RoleMatrixRow>[]>(
    () => [
      {
        id: "member",
        accessorFn: (row) => row.member.name,
        header: "Member",
        cell: ({ row }) => (
          <span className="text-sm font-medium sticky left-0 bg-card block">{row.original.member.name}</span>
        )
      },
      ...projects.map(
        (project) =>
          ({
            id: project.id,
            accessorFn: (row) => {
              const member = row.member
              if (member.role === "admin") return "instance admin"
              return projectMembersMap?.[project.id]?.[member.user_id] ?? ""
            },
            header: project.name,
            cell: ({ row }) => {
              const member = row.original.member
              const role = projectMembersMap?.[project.id]?.[member.user_id]
              if (member.role === "admin") {
                return <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">instance admin</span>
              }
              if (role) {
                return <span className="text-xs bg-secondary px-1.5 py-0.5 rounded">{role.replace("_", " ")}</span>
              }
              return <span className="text-xs">-</span>
            }
          }) as ColumnDef<RoleMatrixRow>
      )
    ],
    [projects, projectMembersMap]
  )

  const roleMatrixRows = useMemo<RoleMatrixRow[]>(() => members.map((member) => ({ member })), [members])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Loading members...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1>Members</h1>
            <p className="text-muted-foreground text-[12px]">Instance</p>
          </div>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowInviteDialog(true)}>
            Invite Member
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto p-3">
            <DataTable columns={memberColumns} data={members} emptyMessage="No members found." showPagination={false} />
          </div>
        </CardContent>
      </Card>

      {isAdmin && projects.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[13px]">Project Role Matrix</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto p-3">
              <DataTable
                columns={roleMatrixColumns}
                data={roleMatrixRows}
                emptyMessage="No members found."
                showPagination={false}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {showInviteDialog && <InviteMemberDialogContainer onClose={() => setShowInviteDialog(false)} />}
    </div>
  )
}
